import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Loader2, Link2, RefreshCw, Power } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Duds size → Printful size labels (XXL becomes "2XL" on Printful)
const SIZE_MAP = { S: "S", M: "M", L: "L", XL: "XL", XXL: "2XL" };

const PrintfulPanel = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [syncProducts, setSyncProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [webhook, setWebhook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingProduct, setSavingProduct] = useState(null);
  const [togglingSetting, setTogglingSetting] = useState(false);
  const [registeringWebhook, setRegisteringWebhook] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, sp, dp, wh] = await Promise.all([
        axios.get(`${API}/admin/printful/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/printful/sync-products`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/printful/webhook`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
      ]);
      setStatus(s.data);
      setSyncProducts(sp.data || []);
      setProducts(dp.data || []);
      setWebhook(wh.data);
    } catch (e) {
      toast.error("Failed to load Printful data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const patchSetting = async (key, value) => {
    setTogglingSetting(true);
    try {
      const res = await axios.patch(
        `${API}/admin/printful/settings`,
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setStatus((s) => ({ ...s, ...res.data }));
      toast.success(`${key.replace("_", " ")} → ${value ? "on" : "off"}`);
    } catch (e) {
      console.warn("[printful] toggle failed:", e);
      toast.error("Failed to update setting.");
    } finally {
      setTogglingSetting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center font-condensed uppercase tracking-widest text-sm text-[#1C1914]/70">
        <Loader2 size={20} className="inline animate-spin mr-2" /> Connecting to Printful…
      </div>
    );
  }

  return (
    <div data-testid="admin-printful-panel" className="space-y-8">
      {/* Connection card */}
      <ConnectionCard status={status} onToggle={patchSetting} disabled={togglingSetting} onRefresh={reload} />

      {!status?.connected && (
        <div className="border-2 border-[#D95D39] bg-[#FFFDF5] p-5">
          <p className="font-display text-xl uppercase text-[#D95D39]">Not connected.</p>
          <p className="font-body text-sm mt-2">
            {status?.error || "Add a valid PRINTFUL_API_KEY in backend/.env and restart the backend."}
          </p>
        </div>
      )}

      {/* Mapping section: only show products that should be fulfilled by Printful */}
      {status?.connected && (
        <WebhookCard
          webhook={webhook}
          registering={registeringWebhook}
          onRegister={async () => {
            const defaultUrl = `${window.location.origin}/api/webhook/printful`;
            const url = window.prompt("Webhook URL to register with Printful:", defaultUrl);
            if (!url) return;
            setRegisteringWebhook(true);
            try {
              const res = await axios.post(
                `${API}/admin/printful/register-webhook`,
                { url },
                { headers: { Authorization: `Bearer ${token}` } },
              );
              setWebhook(res.data);
              toast.success("Webhook registered.");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Webhook registration failed.");
            } finally {
              setRegisteringWebhook(false);
            }
          }}
        />
      )}

      {status?.connected && (
        <div className="space-y-6">
          <div>
            <h2 className="font-display text-2xl uppercase">Variant mapping</h2>
            <p className="font-body text-sm text-[#1C1914]/70 mt-1">
              Link each Duds variant (colour + size) to its matching Printful sync variant.
              Auto-map matches by colour + size name. Only mapped variants get fulfilled by Printful.
            </p>
          </div>
          {products.map((p) => (
              <ProductMappingCard
                key={p.id}
                product={p}
                syncProducts={syncProducts}
                token={token}
                onSave={async (variants) => {
                  setSavingProduct(p.id);
                  try {
                    await axios.put(
                      `${API}/admin/products/${p.id}/printful`,
                      { variants },
                      { headers: { Authorization: `Bearer ${token}` } },
                    );
                    toast.success(`${p.name}: mapping saved (${variants.length} variants).`);
                    await reload();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Save failed.");
                  } finally {
                    setSavingProduct(null);
                  }
                }}
                saving={savingProduct === p.id}
              />
            ))}
        </div>
      )}
    </div>
  );
};

const ConnectionCard = ({ status, onToggle, disabled, onRefresh }) => {
  const ok = status?.connected;
  return (
    <div className="border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {ok ? (
            <CheckCircle2 size={28} className="text-[#4A5D23] flex-shrink-0" />
          ) : (
            <AlertCircle size={28} className="text-[#D95D39] flex-shrink-0" />
          )}
          <div>
            <p className="font-condensed text-xs uppercase tracking-widest text-[#D95D39]">// Printful</p>
            <h2 className="font-display text-3xl uppercase leading-none mt-1">
              {ok ? "Connected." : "Disconnected."}
            </h2>
            {ok && status?.store && (
              <p className="font-body text-sm mt-2">
                Store <strong>{status.store.name}</strong> · ID {status.store.id} · {status.store.type}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="border-2 border-[#1C1914] px-3 py-2 font-condensed text-xs uppercase tracking-widest hover:bg-[#FDE047]"
          data-testid="printful-refresh"
        >
          <RefreshCw size={14} className="inline mr-1" /> Refresh
        </button>
      </div>

      {ok && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 border-t-2 border-dashed border-[#1C1914]/30 pt-5">
          <Toggle
            label="Auto-forward paid orders"
            description="When OFF, paid orders are marked 'manual_required' and you push them by hand."
            enabled={status.enabled}
            onChange={(v) => onToggle("enabled", v)}
            disabled={disabled}
            testId="printful-toggle-enabled"
          />
          <Toggle
            label="Auto-confirm (skip draft step)"
            description="When OFF, orders sit as drafts in Printful until you click Confirm there."
            enabled={status.auto_confirm}
            onChange={(v) => onToggle("auto_confirm", v)}
            disabled={disabled}
            testId="printful-toggle-confirm"
          />
        </div>
      )}
    </div>
  );
};

const WebhookCard = ({ webhook, registering, onRegister }) => {
  const url = webhook?.url;
  const types = webhook?.types || [];
  const isLive = !!url && /dudsapparel\.com/.test(url);
  return (
    <div className="border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard p-5" data-testid="printful-webhook-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-condensed text-xs uppercase tracking-widest text-[#D95D39]">// Webhook</p>
          <h3 className="font-display text-2xl uppercase mt-1">
            {url ? (isLive ? "Live ✓" : "Set (non-prod)") : "Not registered"}
          </h3>
          {url && (
            <p className="font-mono text-xs break-all mt-2 text-[#1C1914]/80">{url}</p>
          )}
          {types.length > 0 && (
            <p className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/60 mt-2">
              Subscribed to: {types.join(", ")}
            </p>
          )}
          {!url && (
            <p className="font-body text-sm text-[#1C1914]/70 mt-2 max-w-xl">
              Register a webhook so Printful can notify Duds when a parcel ships — that's how the
              shipped-confirmation email + tracking link get triggered automatically. Best to do
              this <strong>after</strong> redeploying so the URL points at dudsapparel.com.
            </p>
          )}
        </div>
        <button
          onClick={onRegister}
          disabled={registering}
          className="btn-duds-sm flex-shrink-0"
          data-testid="printful-register-webhook"
        >
          {registering ? "…" : url ? "Re-register" : "Register webhook"}
        </button>
      </div>
    </div>
  );
};

const Toggle = ({ label, description, enabled, onChange, disabled, testId }) => (  <div className="flex items-start gap-3">
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      data-testid={testId}
      className={`relative inline-flex h-7 w-12 flex-shrink-0 border-2 border-[#1C1914] transition-colors mt-1 ${
        enabled ? "bg-[#4A5D23]" : "bg-[#1C1914]/20"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <span
        className={`inline-block h-5 w-5 bg-[#FFFDF5] border-2 border-[#1C1914] transform transition-transform ${
          enabled ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
    <div className="flex-1">
      <p className="font-condensed text-sm uppercase tracking-widest text-[#1C1914]">
        <Power size={12} className="inline mr-1" />
        {label}
      </p>
      <p className="font-body text-xs text-[#1C1914]/60 mt-1">{description}</p>
    </div>
  </div>
);

const ProductMappingCard = ({ product, syncProducts, onSave, saving, token }) => {
  // Each Duds product can have multiple colours; build (color, size) rows
  const rows = useMemo(() => {
    const colors = product.colors?.length ? product.colors : [{ name: null, chip: product.color_chip }];
    return colors.flatMap((c) =>
      product.variants.map((v) => ({
        color: c.name,
        chip: c.chip,
        size: v.size,
      })),
    );
  }, [product]);

  // Initial mapping from product.printful.variants
  const existingByKey = useMemo(() => {
    const map = {};
    for (const v of product.printful?.variants || []) {
      map[`${v.color || ""}::${(v.size || "").toUpperCase()}`] = v;
    }
    return map;
  }, [product]);

  const [mapping, setMapping] = useState(() => {
    const m = {};
    for (const r of rows) {
      const k = `${r.color || ""}::${r.size.toUpperCase()}`;
      m[k] = existingByKey[k] || { color: r.color, size: r.size, sync_variant_id: null };
    }
    return m;
  });

  const flatSyncVariants = useMemo(
    () =>
      syncProducts.flatMap((sp) =>
        sp.variants.map((v) => ({
          ...v,
          sync_product_name: sp.name,
          sync_product_id: sp.sync_product_id,
        })),
      ),
    [syncProducts],
  );

  const setVariant = (key, sync_variant_id) => {
    setMapping((cur) => {
      const next = { ...cur };
      if (!sync_variant_id) {
        next[key] = { ...next[key], sync_variant_id: null, catalog_variant_id: null, printful_name: null };
      } else {
        const hit = flatSyncVariants.find((v) => v.sync_variant_id === Number(sync_variant_id));
        next[key] = {
          ...next[key],
          sync_variant_id: Number(sync_variant_id),
          catalog_variant_id: hit?.catalog_variant_id,
          printful_name: hit?.name,
          sync_product_name: hit?.sync_product_name,
        };
      }
      return next;
    });
  };

  // Build a list of "garment-type" keywords from the Duds product name so
  // auto-map prefers a Printful product whose name shares any of them.
  // We intentionally ignore short/common words to avoid false matches.
  const productKeywords = useMemo(() => {
    const STOP = new Set([
      "the", "a", "an", "and", "or", "of", "with", "duds", "embroidered",
      "premium", "classic", "shirt", "tee",
    ]);
    return (product.name || "")
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((w) => w.length >= 3 && !STOP.has(w));
  }, [product.name]);

  // High-level garment type → restricts which Printful sync products we'll
  // even consider, so we don't auto-link a Hoodie to a Sweatshirt or a Tee.
  const garmentHint = useMemo(() => {
    const n = (product.name || "").toLowerCase();
    if (/hoodie/.test(n)) return /hoodie/;
    if (/crew|sweat/.test(n)) return /sweatshirt|crew/;
    if (/tee|t-shirt|shirt/.test(n)) return /tee|t-shirt|shirt/;
    return null; // no hint → fall back to colour + size matching only
  }, [product.name]);

  const autoMap = () => {
    setMapping((cur) => {
      const next = { ...cur };
      let matched = 0;
      for (const k of Object.keys(next)) {
        if (next[k].sync_variant_id) continue;
        const { color, size } = next[k];
        const pfSize = SIZE_MAP[size?.toUpperCase()] || size;

        // Score every candidate variant: keyword overlap + colour match + size match.
        const scored = flatSyncVariants
          .filter((v) => (v.size || "").toUpperCase() === pfSize.toUpperCase())
          .map((v) => {
            const nameLower = (v.sync_product_name || "").toLowerCase();
            const garmentOk = garmentHint ? garmentHint.test(nameLower) : true;
            const colourOk = color ? nameLower.includes(color.toLowerCase()) : true;
            const keywordHits = productKeywords.filter((w) => nameLower.includes(w)).length;
            return { v, garmentOk, colourOk, keywordHits };
          })
          .filter((s) => s.garmentOk && s.colourOk)
          .sort((a, b) => b.keywordHits - a.keywordHits);

        const candidate = scored[0]?.v;
        if (candidate) {
          next[k] = {
            ...next[k],
            sync_variant_id: candidate.sync_variant_id,
            catalog_variant_id: candidate.catalog_variant_id,
            printful_name: candidate.name,
            sync_product_name: candidate.sync_product_name,
          };
          matched += 1;
        }
      }
      if (matched === 0) {
        toast.error(
          "No matching Printful product found. Create the product in your Printful dashboard first (upload the print file via Design Maker), then come back.",
        );
      } else {
        toast.success(`Auto-mapped ${matched} variant${matched === 1 ? "" : "s"}.`);
      }
      return next;
    });
  };

  const handleSave = () => {
    const variants = Object.values(mapping)
      .filter((v) => v.sync_variant_id)
      .map((v) => ({
        color: v.color,
        size: v.size,
        sync_variant_id: v.sync_variant_id,
        catalog_variant_id: v.catalog_variant_id,
        printful_name: v.printful_name,
      }));
    onSave(variants);
  };

  const mappedCount = Object.values(mapping).filter((v) => v.sync_variant_id).length;
  const totalCount = rows.length;

  // Is there at least one Printful sync product that could plausibly match
  // this Duds product? Used to render a "needs Printful setup" empty state
  // instead of a useless mapping table.
  //   * If we detected a garment family (hoodie/crew/tee) → must match it
  //   * Otherwise → must share at least one keyword with the Duds product name
  const hasMatchingSyncProducts = useMemo(() => {
    if (syncProducts.length === 0) return false;
    return syncProducts.some((sp) => {
      const nameLower = (sp.name || "").toLowerCase();
      if (garmentHint) return garmentHint.test(nameLower);
      if (productKeywords.length === 0) return true;
      return productKeywords.some((w) => nameLower.includes(w));
    });
  }, [garmentHint, productKeywords, syncProducts]);

  // ----- Margin estimator -----
  const [marginCountry, setMarginCountry] = useState("AU");
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);

  const runEstimate = async () => {
    const linked = Object.values(mapping).filter((v) => v.sync_variant_id);
    if (linked.length === 0) {
      toast.error("Link at least one variant before estimating.");
      return;
    }
    setEstimating(true);
    try {
      const res = await axios.post(
        `${API}/admin/printful/estimate`,
        {
          country: marginCountry,
          items: linked.map((v) => ({
            sync_variant_id: v.sync_variant_id,
            retail_usd: product.price_usd,
            label: `${v.color || ""} ${v.size}`,
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEstimate(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Estimate failed.");
    } finally {
      setEstimating(false);
    }
  };

  return (
    <div
      className="border-4 border-[#1C1914] bg-[#FFFDF5] shadow-hard p-5"
      data-testid={`printful-mapping-card-${product.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-display text-2xl uppercase">{product.name}</h3>
          <p className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/70 mt-1">
            {mappedCount} / {totalCount} variants linked · Retail A${product.price_usd?.toFixed(2)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <select
            value={marginCountry}
            onChange={(e) => setMarginCountry(e.target.value)}
            className="border-2 border-[#1C1914] bg-[#FFFDF5] px-2 py-1 font-condensed text-xs uppercase tracking-widest"
            data-testid={`printful-country-${product.id}`}
            aria-label="Margin estimate destination"
          >
            <option value="AU">→ AU</option>
            <option value="US">→ US</option>
            <option value="GB">→ UK</option>
          </select>
          <button
            onClick={runEstimate}
            disabled={estimating || mappedCount === 0}
            className="border-2 border-[#1C1914] px-3 py-2 font-condensed text-xs uppercase tracking-widest hover:bg-[#FDE047] disabled:opacity-50"
            data-testid={`printful-estimate-${product.id}`}
          >
            {estimating ? "Calculating…" : "Estimate margin"}
          </button>
          <button
            onClick={autoMap}
            className="border-2 border-[#1C1914] px-3 py-2 font-condensed text-xs uppercase tracking-widest hover:bg-[#FDE047]"
            data-testid={`printful-automap-${product.id}`}
          >
            <Link2 size={14} className="inline mr-1" /> Auto-map
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-duds-sm"
            data-testid={`printful-save-${product.id}`}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Empty state — no compatible Printful product exists yet */}
      {!hasMatchingSyncProducts && mappedCount === 0 && (
        <div
          className="mb-4 border-2 border-dashed border-[#D95D39] bg-[#FDE047]/30 p-4"
          data-testid={`printful-needs-setup-${product.id}`}
        >
          <p className="font-condensed text-xs uppercase tracking-widest text-[#D95D39]">
            // Needs setup in Printful first
          </p>
          <p className="font-body text-sm mt-2 text-[#1C1914]">
            Couldn't find a matching <strong>{product.name}</strong> in your Printful store.
            To map this product:
          </p>
          <ol className="font-body text-sm mt-2 ml-4 list-decimal space-y-1 text-[#1C1914]/85">
            <li>
              Go to <a href="https://www.printful.com/dashboard/product-templates" target="_blank" rel="noreferrer" className="underline hover:text-[#D95D39]">Printful → Product Templates</a> → <strong>Create product</strong>
            </li>
            <li>Pick the matching blank garment, upload the print file via Design Maker</li>
            <li><strong>Push to your "Duds API" store</strong> from the template</li>
            <li>Come back here, click <strong>Refresh</strong> at the top, then <strong>Auto-map</strong> below</li>
          </ol>
        </div>
      )}

      {/* Margin summary */}
      {estimate && (
        <div
          className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1C1914] border-2 border-[#1C1914]"
          data-testid={`printful-estimate-summary-${product.id}`}
        >
          <SummaryCell
            label="Printful cost / unit"
            value={`A$${estimate.totals_usd.per_unit_printful_cost.toFixed(2)}`}
            sub={`${estimate.printful_breakdown_native.currency} ${estimate.printful_breakdown_native.total.toFixed(2)} → ${estimate.country}`}
          />
          <SummaryCell
            label="Retail / unit"
            value={`A$${product.price_usd.toFixed(2)}`}
            sub="customer pays (AUD)"
          />
          <SummaryCell
            label="Margin / unit"
            value={`A$${estimate.totals_usd.avg_margin_per_unit.toFixed(2)}`}
            sub={`${estimate.totals_usd.margin_pct ?? "—"}% margin`}
            highlight
          />
          <SummaryCell
            label="Cost breakdown"
            value={
              <div className="text-xs font-body leading-tight">
                <div>Subtotal: {estimate.printful_breakdown_native.currency} {estimate.printful_breakdown_native.subtotal.toFixed(2)}</div>
                <div>Shipping: {estimate.printful_breakdown_native.currency} {estimate.printful_breakdown_native.shipping.toFixed(2)}</div>
                {estimate.printful_breakdown_native.digitization > 0 && (
                  <div>Digi (1-off): {estimate.printful_breakdown_native.currency} {estimate.printful_breakdown_native.digitization.toFixed(2)}</div>
                )}
                <div>Tax: {estimate.printful_breakdown_native.currency} {estimate.printful_breakdown_native.tax.toFixed(2)}</div>
              </div>
            }
            sub=""
          />
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1C1914] text-[#FFFDF5] font-condensed uppercase tracking-widest text-xs">
            <tr>
              <th className="text-left px-3 py-2">Duds variant</th>
              <th className="text-left px-3 py-2">Printful sync variant</th>
              <th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="font-body">
            {rows.map((r) => {
              const k = `${r.color || ""}::${r.size.toUpperCase()}`;
              const cur = mapping[k];
              const linked = !!cur?.sync_variant_id;
              return (
                <tr key={k} className="border-t border-[#1C1914]/15" data-testid={`mapping-row-${k}`}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span
                      className="inline-block w-3 h-3 border border-[#1C1914] align-middle mr-2"
                      style={{ background: r.chip }}
                    />
                    {r.color ? `${r.color} · ` : ""}<strong>{r.size}</strong>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="border-2 border-[#1C1914] bg-[#FFFDF5] px-2 py-1 text-sm w-full max-w-[360px]"
                      value={cur?.sync_variant_id || ""}
                      onChange={(e) => setVariant(k, e.target.value)}
                      data-testid={`mapping-select-${k}`}
                    >
                      <option value="">— not linked —</option>
                      {syncProducts.map((sp) => (
                        <optgroup key={sp.sync_product_id} label={sp.name}>
                          {sp.variants.map((v) => (
                            <option key={v.sync_variant_id} value={v.sync_variant_id}>
                              {sp.name} / {v.size} (#{v.sync_variant_id})
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {linked ? (
                      <span className="text-[#4A5D23]">
                        <CheckCircle2 size={14} className="inline mr-1" /> Linked
                      </span>
                    ) : (
                      <span className="text-[#1C1914]/50">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SummaryCell = ({ label, value, sub, highlight }) => (
  <div
    className={`p-3 ${highlight ? "bg-[#FDE047] text-[#1C1914]" : "bg-[#FFFDF5] text-[#1C1914]"}`}
  >
    <div className="font-condensed text-[10px] uppercase tracking-widest text-[#1C1914]/70 leading-none">
      {label}
    </div>
    <div className={`font-display ${typeof value === "string" ? "text-2xl mt-1" : "mt-1"}`}>
      {value}
    </div>
    {sub && (
      <div className="font-condensed text-[10px] uppercase tracking-widest text-[#1C1914]/60 mt-1 leading-none">
        {sub}
      </div>
    )}
  </div>
);

export default PrintfulPanel;
